import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const removeLoadedToolInputSchema = z.object({
  toolKey: z.string(),
});

type Params = z.infer<typeof removeLoadedToolInputSchema>;

export class RemoveLoadedToolAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Unload Tool';
  inputSchema = removeLoadedToolInputSchema;
  group = 'Chat';
  description = 'Remove a tool from the conversation';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: {
        type: 'remove_tool',
        toolKey: params.toolKey,
      },
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to unload tool';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.ToolUnloaded, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      toolKey: payload.toolKey,
    });
  }
}
